import { useState } from 'react';
import { useApi, useToaster } from '../../../utils/hooks';
import { Helpers } from '../../../utils/helpers';
import { ToastStyle } from '../../../utils/hooks/useToaster';

const useSetPasswordModal = (userId: string, onSuccess: () => void, onClose: () => void) => {
  const api = useApi();
  const toaster = useToaster();
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [alertMessage, setAlertMessage] = useState<string>('');

  const updatePassword = (value: string) => {
    setPassword(value);
    clearFieldError('password');
    clearAlertMessage();
  };

  const updateConfirmPassword = (value: string) => {
    setConfirmPassword(value);
    clearFieldError('confirmPassword');
    clearAlertMessage();
  };

  const clearFieldError = (fieldName: string) => {
    setFieldErrors((prev) => prev.filter((error) => error !== fieldName));
  };

  const clearAlertMessage = () => {
    setAlertMessage('');
  };

  const validate = (): boolean => {
    const errors: string[] = [];

    if (Helpers.isNullOrWhiteSpace(password)) {
      errors.push('password');
    }

    if (Helpers.isNullOrWhiteSpace(confirmPassword)) {
      errors.push('confirmPassword');
    }

    if (
      !Helpers.isNullOrWhiteSpace(password) &&
      !Helpers.isNullOrWhiteSpace(confirmPassword) &&
      password !== confirmPassword
    ) {
      errors.push('confirmPassword');
      setAlertMessage('Passwords do not match.');
    }

    if (!Helpers.isNullOrWhiteSpace(password) && password.length < 6) {
      errors.push('password');
      setAlertMessage('Password must be at least 6 characters long.');
    }

    setFieldErrors(errors);
    return errors.length === 0;
  };

  const submitPassword = async (): Promise<void> => {
    if (isSubmitting) return;

    const isValid = validate();
    if (!isValid) {
      return;
    }

    setIsSubmitting(true);
    setAlertMessage('');

    try {
      const endpoint = `/users/${userId}/setpassword`;
      const requestBody = JSON.stringify({
        userId: userId,
        password: password,
        confirmPassword: confirmPassword
      });

      const apiResponse = await api.put<boolean>(endpoint, requestBody);

      if (apiResponse?.status?.statusCode === 200) {
        toaster.addToast('Password set successfully!', ToastStyle.SUCCESS, 3000);
        resetForm();
        onSuccess();
      } else {
        setAlertMessage('Failed to set password. Please try again.');
      }
    } catch (error) {
      setAlertMessage('An error occurred while setting the password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setPassword('');
    setConfirmPassword('');
    setFieldErrors([]);
    setAlertMessage('');
  };

  const closeModal = () => {
    resetForm();
    onClose();
  };

  return {
    password,
    confirmPassword,
    isSubmitting,
    fieldErrors,
    alertMessage,
    updatePassword,
    updateConfirmPassword,
    submitPassword,
    closeModal
  };
};

export default useSetPasswordModal;
